import { useEffect, useState } from 'react';
import { Crown, Trophy, Swords, Zap, Flame } from 'lucide-react';

/**
 * Phase 2M PR 2 — Earned Celebration Overlay.
 *
 * Fires ONLY on culturally significant events:
 *   • championship_clinch     (final match winner crowned)
 *   • comeback_clinched       (comeback story persisted for this tournament)
 *   • streak_broken            (loser had >= 3-match unbeaten run)
 *   • semifinal_win            (winner advances to final)
 *
 * NEVER fires on routine goals — that would burn the emotional scarcity.
 *
 *   <ArenaxCelebrationOverlay event={celebrationEvent} onDismiss={...} />
 */
export default function ArenaxCelebrationOverlay({ event, onDismiss }) {
  const [visible, setVisible] = useState(!!event);

  useEffect(() => {
    if (!event) return;
    setVisible(true);
    const tid = setTimeout(() => {
      setVisible(false);
      if (onDismiss) onDismiss();
    }, 4200);
    return () => clearTimeout(tid);
  }, [event, onDismiss]);

  if (!event || !visible) return null;

  const META = {
    championship_clinch: { Icon: Crown, accent: 'text-amber-200',
                             tint: 'rgba(245,158,11,0.20)',
                             label: 'CHAMPIONS' },
    comeback_clinched: { Icon: Zap, accent: 'text-amber-100',
                          tint: 'rgba(251,113,133,0.18)',
                          label: 'COMEBACK' },
    streak_broken: { Icon: Swords, accent: 'text-rose-200',
                       tint: 'rgba(244,63,94,0.18)',
                       label: 'STREAK BROKEN' },
    semifinal_win: { Icon: Trophy, accent: 'text-amber-200',
                      tint: 'rgba(245,158,11,0.16)',
                      label: 'INTO THE FINAL' },
  };
  const meta = META[event.type] || META.championship_clinch;
  const { Icon, accent, tint, label } = meta;

  return (
    <div
      data-testid={`arenax-celebration-${event.type}`}
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
      aria-hidden="true">
      <div className="absolute inset-0"
        style={{
          background: `radial-gradient(closest-side at 50% 35%, ${tint}, transparent 70%)`,
          animation: 'arenax-celebration-flash 4.2s ease-out',
        }} />
      <div className="relative px-6 py-5 rounded-3xl border border-amber-500/40 bg-slate-950/85 backdrop-blur-sm text-center shadow-2xl"
        style={{ animation: 'arenax-celebration-rise 4.2s ease-out' }}>
        <Icon className={`mx-auto h-10 w-10 ${accent} mb-2`} />
        <div className={`text-[10px] uppercase tracking-[0.4em] font-black ${accent} mb-1`}>
          {label}
        </div>
        <p className="text-base sm:text-lg font-black text-slate-100 leading-snug max-w-md">
          {event.line}
        </p>
      </div>
      <style>{`
        @keyframes arenax-celebration-flash {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes arenax-celebration-rise {
          0%   { opacity: 0; transform: translateY(20px) scale(0.85); }
          15%  { opacity: 1; transform: translateY(0) scale(1); }
          70%  { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-10px) scale(0.95); }
        }
      `}</style>
    </div>
  );
}
